import { IQueryClassDependency } from '@kurtosys/types/dependencies/IQueryClassDependency.js';
import { ITransformClassDependency } from '@kurtosys/types/dependencies/ITransformClassDependency.js';
import { TConditionalHelperDependencies } from '@kurtosys/types/helpers/TConditionalHelperDependencies.js';
import { IExecutionOptions, IQueryOptions } from '@kurtosys/types/query/index.js';
import { IQueryOptionsValueCollectionBase } from '@kurtosys/types/query/options/IQueryOptionsValueCollectionBase.js';

import { helpers } from '../../../common/index.js';

import { BaseCollectionQuery } from './BaseCollectionQuery.js';

export abstract class BaseValueCollectionQuery<
	TChildOptions extends IQueryOptionsValueCollectionBase,
> extends BaseCollectionQuery<TChildOptions> {
	constructor(
		public dependencies: IQueryClassDependency & ITransformClassDependency,
		public queryOptions: IQueryOptions,
		public translationHelper?: helpers.TranslationHelper,
	) {
		super(dependencies, queryOptions, translationHelper);
	}

	getRawValueOrRootKey(executionOptions: IExecutionOptions): any {
		const { rootKey } = this.queryOptions;
		if (rootKey) {
			const collectionItem = this.getCollectionItem(executionOptions);
			return collectionItem[rootKey];
		}
		return this.getRawValueFromSubCollection(executionOptions);
	}
	getRawValueFromSubCollection(executionOptions: IExecutionOptions): any {
		const conditionalHelperDependencies: TConditionalHelperDependencies = {
			queryClass: this.dependencies.queryClass,
		};
		const conditionalHelper = new helpers.ConditionalHelper(
			conditionalHelperDependencies,
			{ conditions: [] },
			this.translationHelper,
		);
		const { key, collectionItemValueConditional } = this.options;
		return this.processEntities(executionOptions, (entity) => {
			if (entity) {
				const collectionItem = this.getCollectionItem(executionOptions);
				const { values = [] } = collectionItem || {};
				const matchingValues = values.filter(
					(value) =>
						!collectionItemValueConditional ||
						conditionalHelper.matchesWithOptions({
							executionOptions,
							instance: value,
							conditional: collectionItemValueConditional,
						}),
				);
				if (matchingValues && matchingValues.length > 0) {
					const item = this.getItem(executionOptions, matchingValues);
					if (item) {
						if (key) {
							return item[key];
						}
						return item;
					}
				}
			}
		});
	}
	getCollectionItem<T = any>(executionOptions: IExecutionOptions): undefined | T {
		const conditionalHelperDependencies: TConditionalHelperDependencies = {
			queryClass: this.dependencies.queryClass,
		};
		const conditionalHelper = new helpers.ConditionalHelper(
			conditionalHelperDependencies,
			{ conditions: [] },
			this.translationHelper,
		);
		const { code, collectionItemConditional = { conditions: [] } } = this.options;
		if (code && !collectionItemConditional.conditions.find((condition): boolean => condition.field === 'code')) {
			collectionItemConditional.conditions.push({
				field: 'code',
				operator: '=',
				value: code,
			});
		}
		const collectionItem = this.getSubCollection(executionOptions)?.find((item): boolean =>
			conditionalHelper.matchesWithOptions({
				executionOptions,
				instance: item,
				conditional: collectionItemConditional,
			}),
		);
		return collectionItem;
	}
}
